import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary/15 text-secondary-foreground border border-secondary/20 hover:bg-secondary/25',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // JusticeLynk gold "Premium" variant
        premium:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90 active:scale-[0.98] border border-secondary/20 shadow-gold-glow/50',
        // Subtle navy outline
        navy: 'border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10',
        // Yellow Gradient
        gradientYellow: 'btn-gradient-yellow',
      },
      size: {
        default: 'h-11 px-8 rounded-lg',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-11 px-8 rounded-lg',
        xl: 'h-12 px-8 rounded-lg text-base',
        icon: 'h-9 w-9 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
